import { useTranslation } from '@mukil/i18n';
import {
  GlassCard,
  InlineAccessDenied,
  LoadingSpinner,
  Stepper,
} from '@jumpstone/ui-react';
import { navigate } from '../../../lib/router';
import { buildStepperSteps, prevStep } from './employee_migration_logic';
import { useMigrationWizard } from './use_migration_wizard';
import { MigrationStartStep } from './migration_start_step';
import { MigrationTemplateStep } from './migration_template_step';
import { MigrationUploadStep } from './migration_upload_step';
import { MigrationMapStep } from './migration_map_step';
import { MigrationReviewStep } from './migration_review_step';
import { MigrationImportStep } from './migration_import_step';
import { MigrationDoneStep } from './migration_done_step';

/**
 * The WagePro migration-import wizard shell (wmi_plan §8). Its one home is
 * Settings -> Data import (`#/settings/import`), so it renders as a settings
 * section under the Settings page header rather than as a page of its own.
 * JSX only — all React state + orchestration lives in `useMigrationWizard`, and
 * every pure transform in the co-located `*_logic.ts` modules. A read-only OAuth
 * grant never reaches the write steps.
 */
export function EmployeeMigrationScreen(): React.JSX.Element {
  const { t } = useTranslation('wp');
  const w = useMigrationWizard();

  const heading = (
    <div className="max-w-xl">
      <h2 className="text-base font-semibold text-[color:var(--ink)]">
        {t('migration.title')}
      </h2>
      <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
        {t('migration.subtitle')}
      </p>
    </div>
  );

  if (w.entityStatus === 'no_entity') {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        {heading}
        <GlassCard className="rounded-2xl p-5">
          <p className="text-sm text-[color:var(--ink-muted)]">
            {t('migration.no_entity')}
          </p>
        </GlassCard>
      </div>
    );
  }

  if (!w.canWrite) {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        {heading}
        <InlineAccessDenied
          title={t('migration.read_only')}
          description={t('migration.subtitle')}
        />
      </div>
    );
  }

  const { wizard } = w;

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {heading}
      <Stepper
        steps={buildStepperSteps(wizard.step, {
          start: t('migration.step.start'),
          template: t('migration.step.template'),
          upload: t('migration.step.upload'),
          map: t('migration.step.map'),
          review: t('migration.step.review'),
          import: t('migration.step.import'),
          done: t('migration.step.done'),
        })}
      />

      {wizard.step === 'start' && (
        <MigrationStartStep
          scope={wizard.scope}
          onScopeChange={w.setScope}
          cutoverDate={wizard.cutover_date}
          onCutoverChange={w.setCutover}
          maxDate={w.today}
          canContinue={w.canContinueStart}
          onNext={() => {
            w.goTo('template');
          }}
        />
      )}

      {wizard.step === 'template' && (
        <MigrationTemplateStep
          templateContext={
            w.templateInfo
              ? {
                  jurisdiction: w.templateInfo.jurisdiction,
                  base_currency: w.templateInfo.base_currency,
                  enabled_currencies: w.templateInfo.enabled_currencies,
                  leave_type_codes: w.templateInfo.leave_type_codes,
                  cutover_date: wizard.cutover_date,
                }
              : null
          }
          existingEmployeeCount={w.templateInfo?.employee_number_count ?? 0}
          onBack={() => {
            w.goTo('start');
          }}
          onNext={() => {
            w.goTo('upload');
          }}
        />
      )}

      {wizard.step === 'template' && !w.templateInfo && (
        <div className="flex justify-center p-4">
          <LoadingSpinner />
        </div>
      )}

      {wizard.step === 'upload' && (
        <MigrationUploadStep
          detectedSheets={w.detectedSheets}
          onParsed={w.setDetectedSheets}
          onBack={() => {
            w.goTo('template');
          }}
          onNext={w.proceedFromUpload}
        />
      )}

      {wizard.step === 'map' && (
        <MigrationMapStep
          detectedSheets={w.detectedSheets}
          jurisdiction={w.jurisdiction}
          initialMappings={w.mappings}
          onBack={() => {
            w.goTo('upload');
          }}
          onNext={w.proceedFromMap}
        />
      )}

      {wizard.step === 'review' && (
        <MigrationReviewStep
          report={w.report}
          loading={w.staging}
          warningCount={w.warningCount}
          acknowledgeWarnings={w.acknowledge}
          onAcknowledgeChange={w.setAcknowledge}
          canImport={w.canImport}
          onBack={() => {
            w.goTo(prevStep('review'));
          }}
          onImport={() => {
            if (w.report) {
              w.goTo('import');
              w.runCommit(w.report.run_id);
            }
          }}
        />
      )}

      {wizard.step === 'import' && (
        <MigrationImportStep
          loaded={w.runReport?.loaded ?? 0}
          total={w.runReport?.total ?? w.report?.total ?? 0}
          running={w.committing}
          stalled={w.commitStatus === 'stalled'}
          hardFailed={w.commitStatus === 'failed'}
          onContinue={() => {
            if (wizard.run_id) w.runCommit(wizard.run_id);
          }}
        />
      )}

      {wizard.step === 'done' && (
        <MigrationDoneStep
          report={w.runReport}
          canUndo={w.canUndo}
          undoing={w.undoing}
          undoError={w.undoError ? t('migration.undo.failed') : null}
          onUndo={w.runUndo}
          onGoToPayroll={() => {
            navigate({ module: 'payroll', tab: 'run' });
          }}
          onDone={() => {
            navigate({ module: 'employees', view: 'directory' });
          }}
        />
      )}
    </div>
  );
}
